// Teams-Tab (design_handoff_team §6, Screens 08–09): Team-Widgets mit Mitglieder-Chips, Inline-Auswahl
// „Mitglied hinzufügen“, Team-Dialog (Name + Teamleitung) und Lösch-Bestätigung. Fachlogik unverändert aus
// TeamsVerwaltung / TeamFormModal: teams, team_members, Teamleitung = Profile team/admin/super admin/master admin,
// die zur Belegschaft gehören.
import { useCallback, useEffect, useState } from 'react';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { CaretUpDown, CircleNotch, CrownSimple, FloppyDisk, PencilSimple, Trash, UserPlus, UsersThree, WarningCircle, X } from '@phosphor-icons/react';
import { supabase } from '../../../../lib/supabase/client';
import { kandidaten, mitgliederText } from '../../../../lib/team/teams';
import { DialogHuelle } from '../../../../components/immobilien/ui';
import { FehlerKarte, FeldRahmen, LadeKarte, LeerKarte, MitarbeiterAvatar } from './Bausteine';
import { GefahrDialog } from './MitarbeiterDialoge';

interface ProfilKurz { id: string; full_name: string; email: string; status?: string | null }
export interface TeamZeile { id: string; name: string; leiter_id?: string | null }

/** Team-Dialog offen: team = null ⇒ neu anlegen. */
export type TeamDialogZustand = { team: TeamZeile | null } | null;

const fehlerText = (err: unknown): string => (err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err));
const name = (p: ProfilKurz) => p.full_name || p.email;

export function TeamsBereich({ dialog, setDialog, melde, onAnzahl }: {
  dialog: TeamDialogZustand;
  setDialog: (d: TeamDialogZustand) => void;
  melde: (text: string, fehler?: boolean) => void;
  onAnzahl: (n: number) => void;
}) {
  const [teams, setTeams] = useState<TeamZeile[]>([]);
  const [mitglieder, setMitglieder] = useState<Record<string, ProfilKurz[]>>({});
  const [alle, setAlle] = useState<ProfilKurz[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinzufuegenFuer, setHinzufuegenFuer] = useState<string | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<TeamZeile | null>(null);
  const [loescht, setLoescht] = useState(false);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const [{ data: t, error: te }, { data: tm, error: me }, { data: p, error: pe }] = await Promise.all([
        supabase.from('teams').select('id, name, leiter_id').order('name'),
        supabase.from('team_members').select('team_id, profile_id'),
        supabase.from('profiles').select('id, full_name, email, status').order('full_name'),
      ]);
      if (te) throw te; if (me) throw me; if (pe) throw pe;
      const profile = (p || []) as ProfilKurz[];
      setAlle(profile);
      setTeams((t || []) as TeamZeile[]);
      onAnzahl((t || []).length);
      const karte: Record<string, ProfilKurz[]> = {};
      ((tm || []) as { team_id: string; profile_id: string }[]).forEach((row) => {
        const prof = profile.find((pr) => pr.id === row.profile_id);
        if (!prof) return;
        (karte[row.team_id] ||= []).push(prof);
      });
      setMitglieder(karte);
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setLaedt(false);
    }
  }, [onAnzahl]);

  useEffect(() => { void laden(); }, [laden]);

  const teamLoeschen = async () => {
    if (!zuLoeschen) return;
    setLoescht(true);
    const { error } = await supabase.from('teams').delete().eq('id', zuLoeschen.id);
    setLoescht(false);
    setZuLoeschen(null);
    if (error) { melde('Fehler: ' + error.message, true); return; }
    void laden();
  };

  const hinzufuegen = async (teamId: string, profileId: string) => {
    const { error } = await supabase.from('team_members').insert({ team_id: teamId, profile_id: profileId });
    if (error) { melde('Fehler: ' + error.message, true); return; }
    setHinzufuegenFuer(null);
    void laden();
  };

  const entfernen = async (teamId: string, profileId: string) => {
    const { error } = await supabase.from('team_members').delete().eq('team_id', teamId).eq('profile_id', profileId);
    if (error) { melde('Fehler: ' + error.message, true); return; }
    void laden();
  };

  const { mobil } = useMobilModus();
  // Handy: Tippflächen 44 px (Knöpfe) bzw. 32 px (X in Chips und Auswahl)
  const k = mobil ? 44 : 36;
  const x = mobil ? 32 : 24;
  const rund = { width: k, height: k, border: 'none', borderRadius: '50%', cursor: 'pointer' } as const;

  return (
    <div className="flex flex-col" style={{ gap: 14 }}>
      <h2 className="m-0" style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink2)' }}>Teams &amp; gemeinsamer Zugriff</h2>

      {laedt ? (
        <LadeKarte />
      ) : fehler ? (
        <FehlerKarte meldung={fehler} onErneut={() => void laden()} />
      ) : teams.length === 0 ? (
        <LeerKarte icon={UsersThree} titel="Noch keine Teams angelegt" />
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: 14 }}>
          {teams.map((team) => {
            const mit = mitglieder[team.id] || [];
            const auswahl = kandidaten(alle, mit);
            return (
              <section key={team.id} aria-label={team.name} className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 26, padding: 18, gap: 14 }}>
                <div className="flex items-center" style={{ gap: 12 }}>
                  <span className="flex shrink-0 items-center justify-center" style={{ width: 44, height: 44, borderRadius: 14, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)' }}>
                    <UsersThree size={22} weight="fill" color="var(--acc-ink)" />
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <h3 className="m-0 truncate" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{team.name}</h3>
                    <span style={{ fontSize: 12.5, color: 'var(--ink3)' }}>{mitgliederText(mit.length)}</span>
                  </div>
                  <button type="button" onClick={() => setDialog({ team })} data-tip="Team umbenennen – Name und Teamleitung ändern" aria-label={`${team.name} umbenennen`}
                    className="flex shrink-0 items-center justify-center" style={{ ...rund, background: 'var(--fill2)', color: 'var(--ink)' }}>
                    <PencilSimple size={15} weight="bold" />
                  </button>
                  <button type="button" onClick={() => setZuLoeschen(team)} data-tip="Team löschen – Die Mitglieder bleiben als Mitarbeiter erhalten" aria-label={`${team.name} löschen`}
                    className="flex shrink-0 items-center justify-center" style={{ ...rund, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                    <Trash size={15} weight="bold" />
                  </button>
                </div>

                <div className="flex flex-wrap" style={{ gap: 6 }}>
                  {mit.length === 0 && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Mitglieder</span>}
                  {mit.map((p) => (
                    <span key={p.id} className="inline-flex min-w-0 items-center" style={{ height: mobil ? 40 : 34, padding: '0 4px', gap: 8, borderRadius: mobil ? 20 : 17, fontSize: 13, fontWeight: 700, background: 'var(--fill2)', maxWidth: '100%' }}>
                      <MitarbeiterAvatar id={p.id} name={name(p)} groesse={26} />
                      <span className="truncate">{name(p)}</span>
                      <button type="button" onClick={() => void entfernen(team.id, p.id)} data-tip={`${name(p)} entfernen – Aus diesem Team nehmen`} aria-label={`${name(p)} entfernen`}
                        className="flex shrink-0 items-center justify-center" style={{ width: x, height: x, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'var(--fill3)', color: 'var(--ink2)' }}>
                        <X size={10} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>

                {hinzufuegenFuer === team.id ? (
                  <FeldRahmen>
                    <UserPlus size={16} color="var(--acc)" />
                    <select autoFocus defaultValue="" aria-label="Mitarbeiter auswählen" onChange={(e) => { if (e.target.value) void hinzufuegen(team.id, e.target.value); }}
                      className="min-w-0 flex-1" style={{ height: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, color: 'var(--ink)', cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
                      <option value="" disabled>Mitarbeiter auswählen…</option>
                      {auswahl.map((p) => <option key={p.id} value={p.id}>{name(p)}</option>)}
                    </select>
                    <button type="button" onClick={() => setHinzufuegenFuer(null)} data-tip="Abbrechen" aria-label="Abbrechen"
                      className="flex shrink-0 items-center justify-center" style={{ width: x + 2, height: x + 2, border: 'none', borderRadius: '50%', cursor: 'pointer', background: 'var(--fill3)', color: 'var(--ink)' }}>
                      <X size={11} weight="bold" />
                    </button>
                  </FeldRahmen>
                ) : (
                  <button type="button" onClick={() => setHinzufuegenFuer(team.id)} data-tip="Mitglied hinzufügen – Mitarbeiter diesem Team zuordnen" className="inline-flex items-center self-start"
                    style={{ height: mobil ? 44 : 38, padding: '0 14px', gap: 7, borderRadius: mobil ? 22 : 19, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                    <UserPlus size={15} weight="bold" />Mitglied hinzufügen
                  </button>
                )}
              </section>
            );
          })}
        </div>
      )}

      {dialog && <TeamDialog team={dialog.team} onSchliessen={() => setDialog(null)} onErfolg={() => void laden()} />}
      {zuLoeschen && (
        <GefahrDialog titel="Team löschen?" icon={UsersThree} knopf="Team löschen" tip="Team löschen – Mitglieder bleiben erhalten" laeuft={loescht}
          text={`Team „${zuLoeschen.name}" wirklich löschen? Die Mitglieder bleiben als Mitarbeiter erhalten.`}
          onBestaetigen={() => void teamLoeschen()} onAbbrechen={() => setZuLoeschen(null)} />
      )}
    </div>
  );
}

/** Team anlegen / umbenennen (Screen 09): Teamname, Teamleitung, Hinweistext; Enter speichert. */
function TeamDialog({ team, onSchliessen, onErfolg }: { team: TeamZeile | null; onSchliessen: () => void; onErfolg: () => void }) {
  const [teamName, setTeamName] = useState(team?.name || '');
  const [leiterId, setLeiterId] = useState(team?.leiter_id || '');
  const [leute, setLeute] = useState<{ id: string; full_name: string | null; email: string | null }[]>([]);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Teamleitung = erste Freigabestufe für Abwesenheiten (wenn mehrstufig eingestellt).
  useEffect(() => {
    void supabase.from('profiles')
      .select('id, full_name, email')
      .in('role', ['team', 'admin', 'super admin', 'master admin'])
      .neq('ist_angestellt', false)
      .order('full_name')
      .then(({ data }) => setLeute(data || []));
  }, []);

  const speichern = async () => {
    const trimmed = teamName.trim();
    if (!trimmed) { setFehler('Bitte einen Namen eingeben.'); return; }
    setSpeichert(true);
    setFehler(null);
    try {
      if (team) {
        const { error } = await supabase.from('teams').update({ name: trimmed, leiter_id: leiterId || null }).eq('id', team.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('teams').insert({ name: trimmed, leiter_id: leiterId || null });
        if (error) throw error;
      }
      onErfolg();
      onSchliessen();
    } catch (err) {
      setFehler(fehlerText(err) || 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const eingabe = { flex: 1, minWidth: 0, height: '100%', border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: 'var(--ink)' } as const;

  return (
    <DialogHuelle titel={team ? 'Team umbenennen' : 'Neues Team'} icon={UsersThree} iconFarbe={{ farbe: 'var(--acc-ink)', flaeche: 'var(--acc-fill)' }}
      onSchliessen={onSchliessen} laeuft={speichert} breite={460}
      fuss={
        <div className="flex w-full flex-wrap justify-end" style={{ gap: 8 }}>
          <button type="button" onClick={onSchliessen} disabled={speichert}
            style={{ height: 46, padding: '0 18px', border: 'none', borderRadius: 15, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, background: 'var(--fill2)', color: 'var(--ink)' }}>
            Abbrechen
          </button>
          <button type="button" onClick={() => void speichern()} disabled={speichert} className="inline-flex items-center"
            style={{ height: 46, padding: '0 20px', gap: 7, border: 'none', borderRadius: 15, cursor: speichert ? 'wait' : 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800,
              background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', opacity: speichert ? 0.7 : 1 }}>
            {speichert ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <FloppyDisk size={15} weight="bold" />}Speichern
          </button>
        </div>
      }>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Teamname</span>
        <FeldRahmen>
          <UsersThree size={16} color="var(--ink3)" />
          <input autoFocus value={teamName} onChange={(e) => setTeamName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void speichern(); }}
            placeholder="Teamname (z.B. Mietverwaltung)" style={eingabe} />
        </FeldRahmen>
      </label>
      <label className="flex flex-col" style={{ gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Teamleitung</span>
        <FeldRahmen>
          <CrownSimple size={16} weight="fill" color="var(--acc)" />
          <select value={leiterId} onChange={(e) => setLeiterId(e.target.value)} style={{ ...eingabe, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}>
            <option value="">— keine —</option>
            {leute.map((p) => <option key={p.id} value={p.id}>{p.full_name || p.email}</option>)}
          </select>
          <CaretUpDown size={13} weight="bold" color="var(--ink3)" />
        </FeldRahmen>
        <span style={{ fontSize: 12, lineHeight: 1.45, color: 'var(--ink3)', padding: '0 4px' }}>
          Erste Freigabestufe für Urlaubsanträge, sofern die Zeiterfassung auf mehrstufige Genehmigung eingestellt ist.
        </span>
      </label>
      {fehler && (
        <div role="alert" className="flex items-center" style={{ borderRadius: 14, padding: '10px 12px', gap: 8, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
          <WarningCircle size={16} weight="fill" className="shrink-0" />{fehler}
        </div>
      )}
    </DialogHuelle>
  );
}
